/**
 * Bottom-sheet primitive — the shared geometry behind all eight overlays in
 * INVENTORY §2.
 *
 * Frozen specification (prototype lines 795-797, 892-894, 922-924, …):
 *   overlay  `position:fixed;inset:0;z-index:80;background:var(--gt-overlay);
 *             display:flex;align-items:flex-end;justify-content:center`
 *   panel    `width:100%;max-width:430px;background:var(--gt-card);
 *             border:1px solid var(--gt-border);border-bottom:0;
 *             box-shadow:0 -10px 0 rgba(0,0,0,.5);display:flex;
 *             flex-direction:column;box-sizing:border-box;
 *             animation:gt-sheet .18s ease-out`
 *   header   `13px 14px`, 1px `--gt-line` bottom, pixel title + mono note +
 *             40×40 ✕ close, sticky when the panel scrolls
 *   close    background click, gated on `ev.target === ev.currentTarget`
 *
 * z-index table (INVENTORY §2): sheets 80 · move sheet 85 · scanlines 90 ·
 * toasts 100 · tooltip 110 · drag ghost 120. Only the move sheet passes
 * `zIndex={85}`.
 *
 * Per-sheet variations are props, not forks:
 *   Filters  `maxHeight="86vh"` + sticky footer in `children`
 *   Move     `zIndex={85}`, no `maxHeight`, custom `header` node
 *   What's New `maxHeight="90vh" hs`
 *   Tracked  `maxHeight="86vh"`
 *
 * Usage (each sheet component reads its own open flag from the store; App only
 * decides whether to mount it):
 *
 *   <Sheet label="Filters sheet" maxHeight="86vh" onClose={close}
 *          header={{ title: '+ FILTERS', note: v.matchTxt }}>
 *     …body…
 *   </Sheet>
 */

import type { CSSProperties, ReactNode } from 'react';

/**
 * The stacking rank a sheet's own CHROME sits at — the sticky header here, and
 * the filter panel's sticky footer, which imports this rather than picking a
 * second number.
 *
 * It has to be a named constant because the invariant is a COMPARISON, not a
 * value: a sticky header shares a stacking context with the content scrolling
 * under it, so anything in the body that ties with it wins on DOM order and
 * paints over it. `filtersVM`'s segmented rows carry 1/2 (a selected segment's
 * border has to overlap its neighbours) and the price bubble carries 6; at the
 * old value of 2 the selected segment drew straight through the header title.
 * `sheetChrome.test.ts` asserts every one of those stays strictly below this.
 */
export const SHEET_CHROME_Z = 7;

/** The ✕ hit target — 40px, and the tallest thing in the header row. */
const SHEET_CLOSE_PX = 40;

/**
 * The header's padding, and it is DELIBERATELY NOT SYMMETRIC (2026-09-02,
 * Marcus: *"add just a bit more padding to the top bar to make it look more
 * centered vertically"*, then *"I still want the modal to take up the full
 * screen I just want the padding on the top to work"*).
 *
 * Centring was measured first and was never the problem: at 412×915 the pixel
 * title's ink, the mono note's ink and the ✕ all sat within 1px of the header's
 * own centre line, at the original 13px. What the row is short of is ROOM ABOVE.
 *
 * And the reason it needs more above than below is that the two edges are not
 * alike. Below the header is a 1px rule and then the sheet's own content, which
 * gives the eye somewhere to stop. Above it, on a full-height sheet, is the
 * phone's browser chrome — a hard edge belonging to something else entirely.
 * Equal padding reads as cramped against that, so the top gets more of it. This
 * is a phone judgement and it applies to every sheet, because every sheet is
 * bottom-anchored and any of them can reach the top.
 */
const SHEET_HEADER_PAD_TOP = 28;
const SHEET_HEADER_PAD_BOTTOM = 17;

/**
 * How tall the sticky header comes out — the ✕, its padding, and the 1px rule.
 *
 * DERIVED RATHER THAN MEASURED, so that anything positioning content BENEATH it
 * cannot drift out of step with the padding above. `FiltersSheet` needs exactly
 * this to scroll the tag picker clear of it; `sheetChrome.test.ts` pins the
 * arithmetic against a real browser measurement.
 */
export const SHEET_HEADER_H =
  SHEET_CLOSE_PX + SHEET_HEADER_PAD_TOP + SHEET_HEADER_PAD_BOTTOM + 1;

export interface SheetHeaderProps {
  /** pixel-font title, e.g. `+ FILTERS` */
  title: ReactNode;
  /** title colour — default `var(--acc)` (What's New uses `var(--gt-info)`) */
  titleClr?: string;
  /** pixel title size — default 9px (What's New uses 8.5px) */
  titleFs?: string;
  /** mono note that fills the row */
  note?: ReactNode;
  /** rendered between the note and the ✕ (e.g. What's New "CLEAR ALL") */
  extra?: ReactNode;
}

export interface SheetProps {
  onClose: () => void;
  children: ReactNode;
  /** 80 for every sheet except the move sheet (85) */
  zIndex?: number;
  /**
   * e.g. `'86vh'`; when set the panel scrolls and the header sticks.
   *
   * **A `vh` value here is resolved against the OVERLAY, not the window** — see
   * the conversion in the component. The prop keeps the prototype's vocabulary;
   * what it means on a phone is "this share of what you can actually see".
   */
  maxHeight?: string;
  /** `data-screen-label`, kept from the prototype for screenshot tooling */
  label?: string;
  /** the standard header row; omit to render a fully custom head in `children` */
  header?: SheetHeaderProps;
  /** hide the panel's scrollbar (`[data-hs]` rule in styles/global.css) */
  hs?: boolean;
  /** merged into the panel, after the frozen base */
  panelStyle?: CSSProperties;
}

export function Sheet({ onClose, children, zIndex, maxHeight, label, header, hs, panelStyle }: SheetProps) {
  const panel: CSSProperties = {
    width: '100%',
    maxWidth: '430px',
    background: 'var(--gt-card)',
    border: '1px solid var(--gt-border)',
    borderBottom: 0,
    boxShadow: '0 -10px 0 rgba(0,0,0,.5)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    animation: 'gt-sheet .18s ease-out',
  };
  if (maxHeight) {
    // `vh` BECOMES A PERCENTAGE OF THE OVERLAY (2026-09-02, Marcus: *"the top
    // bar of the filter modal still looks flush to the top of my phone"*).
    //
    // The two units disagree on a phone, and that disagreement is the whole
    // bug. `vh` resolves against the LARGE viewport — the window as it would
    // be with the browser UI hidden — while `position: fixed; inset: 0` lays
    // this overlay out against what is actually on screen. With Chrome's URL
    // bar showing, the visible strip is roughly 88% of the large viewport, so
    // `86vh` came out at about 97% of it: a sheet that filled the screen and
    // put its header hard against the browser chrome, with none of the 14% gap
    // the number was asking for. Emulators never show this, because there is no
    // URL bar to take the difference.
    //
    // A percentage of the overlay cannot drift that way — the overlay IS the
    // box this sheet is laid out in, by construction — and with
    // `interactive-widget=resizes-content` (see `index.html`) that box already
    // ends at the top of the keyboard. So this one substitution covers both
    // complaints: the sheet is 86% of what is on screen, keyboard or no
    // keyboard, and nothing here has to know which.
    const vh = /^([\d.]+)vh$/.exec(maxHeight);
    panel.maxHeight = vh ? vh[1] + '%' : maxHeight;
    panel.overflowY = 'auto';
  }

  return (
    <div
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
      data-screen-label={label}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: zIndex ?? 80,
        background: 'var(--gt-overlay)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div data-hs={hs ? '1' : undefined} style={{ ...panel, ...panelStyle }}>
        {header ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: `${SHEET_HEADER_PAD_TOP}px 14px ${SHEET_HEADER_PAD_BOTTOM}px`,
              borderBottom: '1px solid var(--gt-line)',
              position: maxHeight ? 'sticky' : 'static',
              top: 0,
              background: 'var(--gt-card)',
              zIndex: SHEET_CHROME_Z,
            }}
          >
            <div
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: header.titleFs ?? '9px',
                color: header.titleClr ?? 'var(--acc)',
                transform: 'translateY(0.13em)',
              }}
            >
              {header.title}
            </div>
            <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '9px', color: 'var(--gt-faint)', flex: 1 }}>
              {header.note}
            </div>
            {header.extra}
            <div
              onClick={onClose}
              style={{
                width: SHEET_CLOSE_PX,
                height: SHEET_CLOSE_PX,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '15px',
                color: 'var(--gt-text-5)',
                cursor: 'pointer',
                flex: 'none',
              }}
            >
              ✕
            </div>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
